/**
 * SyncButton — Bouton de synchronisation globale dans le header
 * Déclenche un event custom pour rafraîchir la page courante
 */

import { useState } from "react";
import { showToast } from "./Toast";

type SyncEventResult = {
  label?: string;
  message?: string;
};

type SyncEventDetail = {
  handled?: boolean;
  promise?: Promise<SyncEventResult | void>;
};

export default function SyncButton() {
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Dispatch a custom event that pages listen to.
      // If no page handles it, fall back to a full reload so the button
      // never appears to do nothing.
      const event = new CustomEvent<SyncEventDetail>("synapse:sync", {
        detail: { handled: false },
      });
      window.dispatchEvent(event);

      if (!event.detail.handled) {
        window.location.reload();
        return;
      }

      const result = event.detail.promise
        ? await event.detail.promise
        : await new Promise<SyncEventResult | void>((resolve) => {
            setTimeout(() => resolve(), 600);
          });

      showToast(
        "success",
        result?.label ?? "Synchronisé",
        result?.message ?? "Données à jour",
      );
    } catch (err) {
      showToast(
        "error",
        "Erreur",
        err instanceof Error ? err.message : "Échec de la synchronisation",
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <button
      className="btn"
      onClick={handleSync}
      disabled={syncing}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        opacity: syncing ? 0.6 : 1,
      }}
    >
      <span
        className={syncing ? "sync-spinner" : ""}
        style={{
          display: "inline-block",
          fontSize: "14px",
          animation: syncing ? "spin 0.8s linear infinite" : undefined,
        }}
      >
        🔄
      </span>
      {syncing ? "Sync…" : "Synchroniser"}
    </button>
  );
}
